import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { Observable } from 'rxjs';
import { DashboardQueryMissingMeteredDataResultDto, DashboardService } from '@app/api/dmc';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { FormApiService } from '@app/core/services/form-api.service';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';
import { Modal } from '@app/core/types/modal-type';

/** Maximum comment length, matching the database column. */
export const COMMENT_MAX_LENGTH = 1000;

/**
 * Editing mode for the modal.
 * - `comment`: only stores the comment
 * - `markHandled`: stores the comment and marks the entries as handled
 */
export type CommentModalMode = 'comment' | 'markHandled';

@Component({
  selector: 'app-missing-timeseries-comment-modal',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    InputGroupComponent,
    LabelDirective,
    InputDirective,
  ],
  templateUrl: './missing-timeseries-comment-modal.component.html',
  styleUrl: './missing-timeseries-comment-modal.component.css',
  providers: [DatePipe],
})
export class MissingTimeseriesCommentModalComponent implements Modal, OnInit, HasUnsavedChanges {

  /** Set by ModalService from params */
  public elements?: DashboardQueryMissingMeteredDataResultDto[];

  /** Set by ModalService from params */
  public mode: CommentModalMode = 'comment';

  public readonly maxLength = COMMENT_MAX_LENGTH;

  public form: FormGroup = new FormGroup({
    meteringPointIdentification: new FormControl<string | null>(null),
    dayOfWork: new FormControl<string | null>(null),
    comment: new FormControl<string | null>(null, Validators.maxLength(COMMENT_MAX_LENGTH)),
  });

  constructor(
    private readonly dashboardService: DashboardService,
    private readonly formApiService: FormApiService,
    private readonly datePipe: DatePipe,
  ) {}

  public get selectedCount(): number {
    return this.elements?.length ?? 0;
  }

  public get single(): DashboardQueryMissingMeteredDataResultDto | undefined {
    return this.selectedCount === 1 ? this.elements![0] : undefined;
  }

  public get commentLength(): number {
    return (this.form.get('comment')?.value ?? '').length;
  }

  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public ngOnInit(): void {
    const element = this.single;
    if (!element) return;

    this.form.patchValue({
      meteringPointIdentification: element.meteringPointIdentification ?? null,
      dayOfWork: this.datePipe.transform(element.dayOfWork, 'yyyy-MM-dd'),
      comment: element.comment ?? null,
    });
  }

  public submit(): void {
    this.formApiService.submit(
      this.form,
      () => this.save(),
      {
        closeModal: true,
        successMessage: this.mode === 'markHandled'
          ? 'dashboard.timeseries.missing.markHandledSuccess'
          : 'dashboard.timeseries.missing.commentSuccess',
      }
    );
  }

  private save(): Observable<unknown> {
    // Read straight from the control: FormApiService strips null values from the
    // submitted payload, and clearing a comment must be sent as an explicit null.
    const comment = this.form.get('comment')?.value || null;

    const items = (this.elements ?? []).map(element => ({
      meteringPointId: element.meteringPointId!,
      dayOfWork: element.dayOfWork,
      businessReasonCode: element.businessReasonCode!,
      dataFlag: element.missingDataStatus!,
      errorMessage: element.errorMessage ?? null,
      comment,
    }));

    if (this.mode === 'markHandled') {
      return this.dashboardService.dashboardMarkMissingMeteredDataHandledPost({ items });
    }

    return this.dashboardService.dashboardSaveMissingMeteredDataCommentPost({ items });
  }
}
